/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {
  Component,
  Input,
  CUSTOM_ELEMENTS_SCHEMA,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges,
  ViewChild,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import type { LeafFileUpload } from '@cigna/leaf-web/lib/wc';
import type { FileWithState } from '@cigna/leaf-web/lib/wc/utilities/file-state-manager';
import '@cigna/leaf-web/lib/wc/components/file-upload/file-upload';

@Component({
  selector: 'cigna-leaf-file-upload',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <leaf-file-upload
      #LeafFileUpload
      [accept]="accept"
      [maxSize]="maxSize"
      [maxFiles]="maxFiles"
      [multiple]="multiple"
      [errorFlag]="errorFlag"
      [defaultLabels]="defaultLabels"
      [requiresAtLeastOneFile]="requiresAtLeastOneFile"
      [disabled]="disabled"
      [isValidatingStage]="isValidatingStage"
      [uploadedFiles]="uploadedFiles"
      [errorNotifications]="errorNotifications"
      [currentStageLabel]="currentStageLabel"
      (leaf-file-selected)="handleFileSelect($event)"
      (leaf-file-delete)="handleFileDelete($event)"
      (leaf-file-dismiss)="handleFileDismiss($event)"
      (leaf-cancel-validation)="handleCancelValidation($event)"
      (leaf-file-processed)="handleFileProcessed($event)"
    ></leaf-file-upload>
  `,
})
export class CignaLeafFileUploadComponent implements OnChanges, AfterViewInit {
  @Input() accept: LeafFileUpload['accept'];
  @Input() maxSize: LeafFileUpload['maxSize'];
  @Input() maxFiles: LeafFileUpload['maxFiles'];
  @Input() multiple: LeafFileUpload['multiple'];
  @Input() disabled: LeafFileUpload['disabled'];
  @Input() defaultLabels: LeafFileUpload['defaultLabels'];
  @Input() requiresAtLeastOneFile = false;
  @Input() errorFlag: LeafFileUpload['errorFlag'];
  @Input() currentStageLabel = '';
  @Input() uploadedFiles: FileWithState[] = [];
  @Input() errorNotifications: FileWithState[] = [];
  @Input() isValidatingStage: LeafFileUpload['isValidatingStage'];

  @ViewChild('LeafFileUpload', { static: false })
  leafFileUpload!: ElementRef<HTMLElement>;

  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onFileSelected = new EventEmitter<CustomEvent>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onFileDeleted = new EventEmitter<File>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onCancelValidation = new EventEmitter<CustomEvent>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onFileProcessed = new EventEmitter<CustomEvent>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onFileDismiss = new EventEmitter<CustomEvent>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['uploadedFiles'] || changes['errorNotifications']) {
      this.updateFileArrays();
    }
  }

  ngAfterViewInit(): void {
    if (this.uploadedFiles.length > 0 || this.errorNotifications.length > 0) {
      this.updateFileArrays();
    }
  }

  private updateFileArrays(): void {
    if (!this.leafFileUpload || !this.leafFileUpload.nativeElement) {
      return;
    }
    const leafWebComponent = this.leafFileUpload
      .nativeElement as unknown as LeafFileUpload;
    leafWebComponent.uploadedFiles = this.uploadedFiles;
    leafWebComponent.errorNotifications = this.errorNotifications;
    leafWebComponent.requestUpdate();
  }

  handleFileSelect(event: Event): void {
    this.onFileSelected.emit(event as CustomEvent);
  }

  handleFileDelete(event: Event): void {
    const customEvent = event as CustomEvent;
    const deletedFile = customEvent.detail?.file;
    if (deletedFile) {
      // Emit the delete event immediately without restoration logic
      // Let the parent component handle the confirmation modal and data management
      this.onFileDeleted.emit(deletedFile);
    }
  }

  handleFileDismiss(event: Event): void {
    // Forward the dismiss event to parent components so they can react (e.g., restore a dismissed file)
    this.onFileDismiss.emit(event as CustomEvent);
  }

  handleCancelValidation(event: Event): void {
    this.onCancelValidation.emit(event as CustomEvent);
  }

  handleFileProcessed(event: Event): void {
    this.onFileProcessed.emit(event as CustomEvent);
  }
}
